// ---------------------------------------------------------------------------
// Icons across the CRM: section titles, number tiles, menu tabs, Setup links and the main buttons get a line icon,
// coloured by meaning (blue sales, green time, orange risk, purple documents, teal intelligence, amber money,
// slate settings). The icon is chosen from the element's text, so every page gets them without changing each page.
// Only data attributes are added to the page (React keeps owning the content); the icons themselves are CSS.
// ---------------------------------------------------------------------------

type Tone = 'blue' | 'green' | 'orange' | 'purple' | 'teal' | 'amber' | 'slate' | 'indigo' | 'cyan' | 'pink';
const TONES: Record<Tone, { fg: string; bg: string }> = {
  indigo: { fg: '#4338ca', bg: '#eceafd' },
  cyan: { fg: '#0e7490', bg: '#e2f4f8' },
  pink: { fg: '#be185d', bg: '#fce7f1' },
  blue: { fg: '#0b5cab', bg: '#e8f0fb' },
  green: { fg: '#15803d', bg: '#e7f5ec' },
  orange: { fg: '#c2410c', bg: '#fdeee6' },
  purple: { fg: '#7c3aed', bg: '#f1ebfd' },
  teal: { fg: '#0d9488', bg: '#e3f5f3' },
  amber: { fg: '#b45309', bg: '#fbf1e2' },
  slate: { fg: '#475467', bg: '#eef0f4' },
};

/** Line icons (24 × 24, stroke 1.75): the CRM's one icon family, also drawn in text and buttons by components/Icon.tsx. */
export const ICONS: Record<string, { d: string; tone: Tone }> = {
  attach: { d: 'M16.5 7.5 9 15a2.1 2.1 0 0 0 3 3l7.5-7.5a3.9 3.9 0 0 0-5.5-5.5L6.5 12.5a5.7 5.7 0 0 0 8 8L19 16', tone: 'slate' },
  undo: { d: 'M9 14 4 9l5-5 M4 9h10a6 6 0 0 1 0 12h-3', tone: 'slate' },
  skip: { d: 'M5 5l8 7-8 7z M17 5v14', tone: 'cyan' },
  close: { d: 'M6 6l12 12 M18 6 6 18', tone: 'slate' },
  hourglass: { d: 'M7 3h10 M7 21h10 M8 3c0 5 8 5 8 9s-8 4-8 9 M16 3c0 5-8 5-8 9', tone: 'amber' },
  thumbsUp: { d: 'M7 11v9H4v-9z M7 11l4-7a2 2 0 0 1 3 2l-1 4h5a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 16.8 20H7', tone: 'green' },
  image: { d: 'M4 5h16v14H4z M4 16l5-5 4 4 2-2 5 5 M15.5 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', tone: 'cyan' },
  drawing: { d: 'M4 17 17 4l3 3L7 20z M8 13l2 2 M11 10l2 2 M14 7l2 2', tone: 'purple' },
  archive: { d: 'M4 4h16v5H4z M6 9v11h12V9 M10 13h4', tone: 'amber' },
  camera: { d: 'M4 8h3.5L9 5.5h6L16.5 8H20v11H4z M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z', tone: 'slate' },
  inbox: { d: 'M4 13l2.5-8h11L20 13v6H4z M4 13h5l1 2h4l1-2h5', tone: 'indigo' },
  checkCircle: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M8 12.5l2.7 2.7L16 9.8', tone: 'green' },
  xCircle: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M9 9l6 6 M15 9l-6 6', tone: 'orange' },
  move: { d: 'M12 3v18 M3 12h18 M12 3 9 6 M12 3l3 3 M12 21l-3-3 M12 21l3-3 M3 12l3-3 M3 12l3 3 M21 12l-3-3 M21 12l-3 3', tone: 'slate' },
  forward: { d: 'M15 5l5 5-5 5 M20 10H9a5 5 0 0 0-5 5v4', tone: 'slate' },
  pause: { d: 'M9 5v14 M15 5v14', tone: 'amber' },
  pin: { d: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z M12 12a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4z', tone: 'green' },
  bulb: { d: 'M9 18h6 M10 21h4 M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z', tone: 'amber' },
  mic: { d: 'M12 14.5a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5.5a3 3 0 0 0 3 3z M6.5 11.5a5.5 5.5 0 0 0 11 0 M12 17v3.5', tone: 'purple' },
  eye: { d: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', tone: 'slate' },
  phone: { d: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z', tone: 'green' },
  mobile: { d: 'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z M11 18h2', tone: 'slate' },
  idCard: { d: 'M3 6h18v12H3z M8.5 12.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M5.5 16c.5-1.6 1.6-2.4 3-2.4s2.5.8 3 2.4 M14 10h4 M14 13h4', tone: 'indigo' },
  box: { d: 'M12 3 20 7.5v9L12 21l-8-4.5v-9z M4 7.5 12 12l8-4.5 M12 12v9', tone: 'amber' },
  stop: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M5.6 5.6l12.8 12.8', tone: 'orange' },
  star: { d: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z', tone: 'amber' },
  skipBack: { d: 'M19 5l-8 7 8 7z M7 5v14', tone: 'cyan' },
  speaker: { d: 'M4 9.5v5h3.5L13 19V5L7.5 9.5z M16.5 9a4 4 0 0 1 0 6', tone: 'purple' },
  expand: { d: 'M4 9V4h5 M20 9V4h-5 M4 15v5h5 M20 15v5h-5', tone: 'slate' },
  globe: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M3 12h18 M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9 M12 3C9.5 5.6 8.2 8.6 8.2 12s1.3 6.4 3.8 9', tone: 'cyan' },
  pointer: { d: 'M9 11V5a1.5 1.5 0 0 1 3 0v5 M12 10V8.5a1.5 1.5 0 0 1 3 0V11 M15 10.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-.5a6 6 0 0 1-5-2.7L4 15.5a1.6 1.6 0 0 1 2.6-1.8L9 16', tone: 'indigo' },
  sun: { d: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 2.5v2 M12 19.5v2 M4.6 4.6 6 6 M18 18l1.4 1.4 M2.5 12h2 M19.5 12h2 M4.6 19.4 6 18 M18 6l1.4-1.4', tone: 'amber' },
  moon: { d: 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z', tone: 'indigo' },
  square: { d: 'M5 5h14v14H5z', tone: 'slate' },
  checkSquare: { d: 'M5 5h14v14H5z M8.5 12l2.5 2.5 4.5-5', tone: 'green' },
  signal: { d: 'M5 18v-2 M9.5 18v-5 M14 18v-8 M18.5 18V7', tone: 'green' },
  filter: { d: 'M4 5h16l-6 7.5V18l-4 2v-7.5z', tone: 'slate' },
  columns: { d: 'M4 5h16v14H4z M10 5v14 M16 5v14', tone: 'slate' },
  copy: { d: 'M9 9h11v11H9z M5 15H4V4h11v1', tone: 'slate' },
  briefcase: { d: 'M3 7h18v13H3z M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M3 12h18', tone: 'blue' },
  user: { d: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21c1-4 4-6 8-6s7 2 8 6', tone: 'blue' },
  users: { d: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M2.5 20c.7-3.4 3.2-5.3 6.5-5.3s5.8 1.9 6.5 5.3 M16 4.5a3.3 3.3 0 0 1 0 6.3 M18 14.8c2 .6 3.2 2.4 3.6 5.2', tone: 'blue' },
  building: { d: 'M5 21V4h10v17 M15 9h4v12 M3 21h18 M8 8h2 M8 12h2 M8 16h2', tone: 'blue' },
  home: { d: 'M3 11 12 4l9 7 M5 10v10h14V10 M10 20v-5h4v5', tone: 'blue' },
  chat: { d: 'M4 5h16v11H9l-5 4z M8 10h8', tone: 'blue' },
  mail: { d: 'M3 6h18v12H3z M3 7l9 6 9-6', tone: 'blue' },
  stage: { d: 'M4 7h4l8 10h4 M4 17h4l2-2.5 M14 9.5 16 7h4 M18 5l2 2-2 2 M18 15l2 2-2 2', tone: 'blue' },
  chart: { d: 'M4 20V11 M10 20V5 M16 20v-7 M21 20H3', tone: 'teal' },
  trend: { d: 'M3 17l6-6 4 4 8-8 M15 7h6v6', tone: 'teal' },
  target: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z M12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z', tone: 'teal' },
  sparkle: { d: 'M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z', tone: 'teal' },
  percent: { d: 'M19 5 5 19 M7 5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z M17 15a2 2 0 1 0 0 4 2 2 0 0 0 0-4z', tone: 'teal' },
  clock: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 7v5l3 2', tone: 'green' },
  calendar: { d: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4', tone: 'green' },
  refresh: { d: 'M20 11a8 8 0 0 0-14.5-4.5L4 8 M4 4v4h4 M4 13a8 8 0 0 0 14.5 4.5L20 16 M20 20v-4h-4', tone: 'green' },
  list: { d: 'M9 6h12 M9 12h12 M9 18h12 M4 6h.01 M4 12h.01 M4 18h.01', tone: 'slate' },
  // Logistics: a delivery truck, in the same slate as before (only the picture changes).
  truck: { d: 'M2 6h11v10H2z M13 9h4l3 3.5V16h-7 M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z', tone: 'slate' },
  task: { d: 'M4 4h16v16H4z M8 12l3 3 5-6', tone: 'green' },
  file: { d: 'M6 3h8l4 4v14H6z M14 3v4h4 M9 13h6 M9 17h6', tone: 'purple' },
  folder: { d: 'M3 6h6l2 2h10v11H3z', tone: 'purple' },
  send: { d: 'M21 3 3 10l7 3 3 7z M10 13l5-5', tone: 'purple' },
  tag: { d: 'M3 12V4h8l10 10-8 8z M7.5 7.5h.01', tone: 'purple' },
  alert: { d: 'M12 4 2 20h20z M12 10v4 M12 17h.01', tone: 'orange' },
  flag: { d: 'M5 21V4 M5 4h12l-2 4 2 4H5', tone: 'orange' },
  trophy: { d: 'M8 4h8v5a4 4 0 0 1-8 0z M8 6H4a4 4 0 0 0 4 4 M16 6h4a4 4 0 0 1-4 4 M12 13v4 M8 21h8 M10 17h4', tone: 'amber' },
  money: { d: 'M3 6h18v12H3z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M6 9v.01 M18 15v.01', tone: 'green' },
  // Accounts (the department), invoices, purchases and project money: each its own symbol and colour.
  bank: { d: 'M3 10 12 4l9 6 M5 10v8 M9.5 10v8 M14.5 10v8 M19 10v8 M3 20h18', tone: 'indigo' },
  receipt: { d: 'M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6 M9 12h6 M9 16h3', tone: 'purple' },
  // Expenses (receipts paid by the employee): a receipt with a coin.
  expense: { d: 'M5 3h10v18l-2.5-1.7L10 21l-2.5-1.7L5 21z M8 8h4 M8 12h2 M18 11a3 3 0 1 0 0 6 3 3 0 0 0 0-6z', tone: 'pink' },
  cart: { d: 'M3 4h2l2.4 11h10.2L20 8H6.3 M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M17 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z', tone: 'cyan' },
  wallet: { d: 'M4 7h15a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z M4 7l11-3v3 M16 13h.01', tone: 'teal' },
  project: { d: 'M3 21h18 M5 21V9l7-5 7 5v12 M9 21v-6h6v6', tone: 'blue' },
  wrench: { d: 'M15 4a5 5 0 0 0-5 6l-6 6 4 4 6-6a5 5 0 0 0 6-5l-3 3-3-1-1-3z', tone: 'slate' },
  toolbox: { d: 'M3 9h18v11H3z M9 9V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3 M3 14h18 M10 13v2 M14 13v2', tone: 'purple' },
  gear: { d: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M12 2v3 M12 19v3 M2 12h3 M19 12h3 M4.9 4.9 7 7 M17 17l2.1 2.1 M4.9 19.1 7 17 M17 7l2.1-2.1', tone: 'slate' },
  shield: { d: 'M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6z M9 12l2 2 4-4', tone: 'slate' },
  lock: { d: 'M5 11h14v10H5z M8 11V7a4 4 0 0 1 8 0v4', tone: 'slate' },
  link: { d: 'M9 15l6-6 M10 6l1-1a4 4 0 0 1 6 6l-1 1 M14 18l-1 1a4 4 0 0 1-6-6l1-1', tone: 'slate' },
  zap: { d: 'M13 2 4 14h7l-1 8 9-12h-7z', tone: 'amber' },
  database: { d: 'M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3z M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6 M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3', tone: 'slate' },
  bell: { d: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4z M10 20a2 2 0 0 0 4 0', tone: 'orange' },
  // Work states (My Desk and similar counters): each its own symbol.
  userPlus: { d: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M2 21c.8-3.8 3.5-6 7-6s6.2 2.2 7 6 M19 8v6 M16 11h6', tone: 'indigo' },
  play: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M10 8.5v7l6-3.5z', tone: 'cyan' },
  alarm: { d: 'M12 6a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M12 10v3l2 2 M4 5l3-2 M20 5l-3-2', tone: 'pink' },
  help: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7 M12 17h.01', tone: 'amber' },
  // Buttons.
  plus: { d: 'M12 5v14 M5 12h14', tone: 'blue' },
  edit: { d: 'M4 20h4L19 9l-4-4L4 16z M14 6l4 4', tone: 'blue' },
  check: { d: 'M5 12l5 5 9-10', tone: 'green' },
  upload: { d: 'M12 16V4 M7 9l5-5 5 5 M4 20h16', tone: 'purple' },
  download: { d: 'M12 4v12 M7 11l5 5 5-5 M4 20h16', tone: 'purple' },
  print: { d: 'M7 9V3h10v6 M5 9h14v8H5z M8 14h8v7H8z', tone: 'slate' },
  trash: { d: 'M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13', tone: 'orange' },
  search: { d: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M20 20l-4-4', tone: 'slate' },
  // The CEO page: a crown.
  crown: { d: 'M4 18h16 M4 8l4 4 4-6 4 6 4-4-2 10H6z', tone: 'teal' },
};

/** Text → icon: the first rule that matches wins. */
const RULES: [RegExp, string][] = [
  // Project tabs and other specific sections first.
  [/^ceo$/, 'crown'],
  [/^purchasing$|purchase request/, 'cart'],
  [/workbench|my desk/, 'toolbox'],
  // Work-state counters (e.g. My Desk tiles).
  [/high priority/, 'zap'],
  [/^\W*to assign|unassigned/, 'userPlus'],
  [/coming next/, 'play'],
  [/support to accept/, 'users'],
  [/performance/, 'trend'],
  [/next \d+ days/, 'calendar'],
  [/profit/, 'money'],
  [/customer requests/, 'inbox'],
  [/to acknowledge|not acknowledged/, 'bell'],
  [/^\W*in progress/, 'play'],
  [/^\W*late\b/, 'alarm'],
  [/due in \d+ days?|due soon/, 'calendar'],
  [/missing information|missing data/, 'help'],
  // The accounts department (Odoo invoicing), not companies; its work, each with its own symbol.
  [/^accounts$|odoo/, 'bank'],
  [/^expenses$|my expenses|expense claim/, 'expense'],
  [/invoice/, 'receipt'],
  [/purchase|supplier order/, 'cart'],
  [/projects money|cash ?flow|payment|collected/, 'wallet'],
  [/won deals|deals won/, 'trophy'],
  [/team overview|salesperson scorecard|by salesperson|stakeholder/, 'users'],
  [/testing|snag|quality|safety|inspection/, 'shield'],
  [/handover|warranty/, 'check'],
  [/^service|ticket/, 'wrench'],
  [/planning|timeline|look-?ahead|schedule/, 'calendar'],
  [/boq|material|points/, 'list'],
  [/purchase|invoice|billing|money/, 'money'],
  [/follow-?up|productivity/, 'task'],
  [/overview|summary|welcome to setup/, 'home'],
  [/closing/, 'target'],
  [/intelligence|next best action|brief|scenario|agent/, 'sparkle'],
  [/risk|attention|warning|overdue|alert|critical|stalled/, 'alert'],
  [/competitor/, 'flag'],
  [/win \/ loss|win\/loss|won|loss reason|trophy/, 'trophy'],
  [/brand/, 'tag'],
  [/submittal|hand-?off/, 'send'],
  [/clarification/, 'chat'],
  [/workload/, 'users'],
  [/on-?hand/, 'briefcase'],
  [/^logistics$/, 'truck'],
  [/document|files|file|costing|offer/, 'file'],
  [/folder/, 'folder'],
  [/quotation|gp|margin|commercial|revenue|value|amount|price|discount|budget|pipeline value|open value|expected gross|weighted|commit/, 'money'],
  [/completeness|confidence|probability|readiness|accuracy|%/, 'percent'],
  [/score|kpi|target|goal|quota/, 'target'],
  [/forecast|trend/, 'trend'],
  [/pipeline|report|dashboard|analysis|chart|by salesperson|by month/, 'chart'],
  [/expected po|po date|calendar|event|close date|deadline|this week|this month|next month/, 'calendar'],
  [/aging|stage aging|tender|time|days|hours|clock/, 'clock'],
  [/stage/, 'stage'],
  [/task|to-?do|my work/, 'task'],
  [/history|log|recent|audit trail|list/, 'list'],
  [/chat|message|comment|note/, 'chat'],
  [/email|mail/, 'mail'],
  [/approval/, 'shield'],
  [/project|delivery|site/, 'project'],
  [/tool|calibration/, 'wrench'],
  [/manpower|team|pre-?sales|people|users|roles|groups|queues|contact roles|stakeholder/, 'users'],
  [/contact|person|profile|owner/, 'user'],
  [/compan|account|business details|customer/, 'building'],
  [/opportunit|deal/, 'briefcase'],
  [/home|welcome/, 'home'],
  [/security|sign-in|sign in|sharing|audit|password|two-step/, 'lock'],
  [/integration|microsoft|teams|slack|webhook/, 'link'],
  [/automation|workflow|process/, 'zap'],
  [/import|data|recycle|configuration|transfer/, 'database'],
  [/notification|reminder/, 'bell'],
  [/setting|setup|object manager|fields|rules|layout/, 'gear'],
];

/** Buttons: exact action words. */
const BUTTONS: [RegExp, string][] = [
  [/^\+?\s*(new|add|create|new task|new event)\b/, 'plus'],
  [/^(edit|change)\b/, 'edit'],
  [/^(save|accept|approve|done|confirm)\b/, 'check'],
  [/^(upload|import)\b/, 'upload'],
  [/^(download|export)\b/, 'download'],
  [/^print/, 'print'],
  [/^(delete|remove)\b/, 'trash'],
  [/^refresh\b/, 'refresh'],
  [/^(search|find)\b/, 'search'],
  // Every other common action and place, so a button has an icon wherever it is.
  [/^(cancel|close|discard|dismiss)\b/, 'close'],
  [/^(reject|refuse|decline)\b/, 'xCircle'],
  [/^(send|submit|resubmit)\b/, 'send'],
  [/^(assign|reassign)\b/, 'userPlus'],
  [/^(start|run|begin|play|watch)\b/, 'play'],
  [/^(open|go to|view all|see all|continue)\b/, 'forward'],
  [/^filters?\b/, 'filter'],
  [/^columns?\b/, 'columns'],
  [/^(copy|duplicate|clone)\b/, 'copy'],
  [/^(show|view|preview|hide)\b/, 'eye'],
  [/^(retry|sync|update|recalculate|check)\b/, 'refresh'],
  [/^(reset|restore|undo|revert|back)\b/, 'undo'],
  [/^(mark|apply|acknowledge|complete|finish|ok|got it|yes)\b/, 'check'],
  [/^(call|log a call)\b/, 'phone'],
  [/^(email|reply|respond)\b/, 'mail'],
  [/^(scan|take a photo|photos?|selfie)\b/, 'camera'],
  [/^(schedule|book)\b/, 'calendar'],
  [/^(pay|record a payment)\b/, 'money'],
  [/^archive\b/, 'archive'],
  [/^(disable|deactivate|revoke|stop|turn off|block)\b/, 'stop'],
  [/^(enable|activate|turn on|connect|link|reconnect)\b/, 'link'],
  [/^(disconnect|unlink)\b/, 'close'],
  [/^(move|transfer)\b/, 'forward'],
  [/^(arrange|reorder|customi[sz]e)\b/, 'move'],
  [/^(ask|chat|comment)\b/, 'chat'],
  [/^request\b/, 'inbox'],
  [/^sign\b/, 'edit'],
  [/^install\b/, 'mobile'],
  [/^(manpower|team|people|users)\b/, 'users'],
  [/^tools?\b/, 'wrench'],
  [/^(portfolio|dashboard|reports?|kpis?|forecasts?)\b/, 'chart'],
  [/^(profit|cash|prices?|costing)\b/, 'money'],
  [/^odoo/, 'link'],
  [/compliance/, 'shield'],
  [/^(settings|setup)\b/, 'gear'],
  [/^(history|timeline)\b/, 'clock'],
  [/^my desk\b/, 'toolbox'],
  [/^(help|guide)\b/, 'help'],
];

const pick = (text: string, rules: [RegExp, string][]) => rules.find(([re]) => re.test(text))?.[1] ?? null;
const uri = (d: string, stroke = 'black') => `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${stroke}' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='${d}'/></svg>`)}")`;

/** The colour mixed with 60% white: the symbol on a soft tile in dark mode. */
const lighter = (hex: string) => `#${[1, 3, 5].map((i) => Math.round(parseInt(hex.slice(i, i + 2), 16) * 0.4 + 153).toString(16).padStart(2, '0')).join('')}`;

/**
 * How a clickable icon moves on hover or keyboard focus, once: the way its action works (refresh turns, upload rises,
 * send lifts off…). Icons not listed lift 2px. Only buttons, links and tabs move; titles stay still.
 */
export const MOVES: Record<string, string> = {
  attach: 'rotate(-12deg)', undo: 'rotate(-25deg)', skip: 'translateX(2px)', close: 'rotate(90deg)', hourglass: 'rotate(180deg)',
  thumbsUp: 'rotate(-10deg) translateY(-1px)', camera: 'scale(1.1)', forward: 'translateX(2px)', move: 'scale(1.12)', folder: 'translateY(-1px)',
  file: 'translateY(-1px)', inbox: 'translateY(1px)', flag: 'rotate(-8deg)', money: 'translateY(-1px) scale(1.06)', checkCircle: 'scale(1.12)',
  pause: 'scale(1.12)', pin: 'translateY(-2px)', bulb: 'scale(1.12)', mic: 'scale(1.12)', eye: 'scale(1.08)', phone: 'rotate(-12deg)', star: 'rotate(20deg)',
  speaker: 'scale(1.1)', expand: 'scale(1.12)', sun: 'rotate(45deg)', moon: 'rotate(-15deg)', pointer: 'translateY(-2px)', box: 'translateY(-1px)',
  skipBack: 'translateX(-2px)', checkSquare: 'scale(1.1)', globe: 'rotate(20deg)',
  refresh: 'rotate(180deg)', upload: 'translateY(-2px)', download: 'translateY(2px)', send: 'translate(2px, -2px)', trash: 'rotate(-10deg)',
  plus: 'scale(1.25)', check: 'scale(1.15)', edit: 'rotate(-12deg)', search: 'translate(-1px, -1px) scale(1.06)', print: 'translateY(1px)',
  play: 'translateX(2px)', link: 'rotate(-20deg)', gear: 'rotate(60deg)', mail: 'translateY(-1px) scale(1.05)', truck: 'translateX(2px)',
  wrench: 'rotate(-20deg)', users: 'translateY(-1px)', filter: 'translateY(1px)', copy: 'translate(1px, 1px)', userPlus: 'translateY(-1px)',
  chart: 'scaleY(1.12)', trend: 'translate(1px, -1px)', calendar: 'scale(1.08)', clock: 'rotate(30deg)', alarm: 'rotate(12deg)', zap: 'scale(1.15)',
};

function css(): string {
  // --ico: the symbol as a mask; --ico-w: the same symbol drawn in white, for small solid tiles made of one pseudo-element;
  // --ico-c / --ico-cl: drawn in its colour (and lighter, for dark mode), for soft tiles made of one pseudo-element.
  const icons = Object.entries(ICONS).map(([name, i]) => `[data-ico="${name}"]{--ico:${uri(i.d)};--ico-w:${uri(i.d, 'white')};--ico-c:${uri(i.d, TONES[i.tone].fg)};--ico-cl:${uri(i.d, lighter(TONES[i.tone].fg))};--ico-fg:${TONES[i.tone].fg};--ico-bg:${TONES[i.tone].bg}${MOVES[name] ? `;--ico-move:${MOVES[name]}` : ''}${name === 'bell' ? ';--ico-anim:m-ico-swing' : ''}}`).join('\n');
  return `${icons}
[data-ico-sq]{position:relative;padding-inline-start:40px!important;min-height:30px;display:flex;align-items:center;gap:6px}
[data-ico-sq]::before{content:"";position:absolute;inset-inline-start:0;top:50%;transform:translateY(-50%);width:30px;height:30px;border-radius:9px;background:var(--ico-fg);box-shadow:0 2px 6px color-mix(in srgb,var(--ico-fg) 30%,transparent),inset 0 1px 0 rgb(255 255 255 / 22%)}
[data-ico-sq]::after{content:"";position:absolute;inset-inline-start:7px;top:50%;transform:translateY(-50%);width:16px;height:16px;background:#fff;-webkit-mask:var(--ico) center/contain no-repeat;mask:var(--ico) center/contain no-repeat}
[data-ico-k]{position:relative;padding-inline-start:28px!important;display:inline-flex;align-items:center;min-height:22px}
[data-ico-k]::before{content:"";position:absolute;inset-inline-start:0;top:50%;transform:translateY(-50%);width:22px;height:22px;border-radius:7px;background:var(--ico-fg);box-shadow:0 2px 5px color-mix(in srgb,var(--ico-fg) 28%,transparent),inset 0 1px 0 rgb(255 255 255 / 22%)}
[data-ico-k]::after{content:"";position:absolute;inset-inline-start:5px;top:50%;transform:translateY(-50%);width:12px;height:12px;background:#fff;-webkit-mask:var(--ico) center/contain no-repeat;mask:var(--ico) center/contain no-repeat}
[data-ico-t]{position:relative;padding-inline-start:40px!important}
[data-ico-t]::before{content:"";position:absolute;inset-inline-start:10px;top:50%;width:24px;height:24px;margin-top:-12px;border-radius:7px;background:var(--ico-fg);box-shadow:0 2px 5px color-mix(in srgb,var(--ico-fg) 30%,transparent),inset 0 1px 0 rgb(255 255 255 / 22%);transition:transform .15s,box-shadow .15s}
[data-ico-t]::after{content:"";position:absolute;inset-inline-start:15px;top:50%;width:14px;height:14px;margin-top:-7px;background:#fff;-webkit-mask:var(--ico) center/contain no-repeat;mask:var(--ico) center/contain no-repeat;transition:transform .15s;pointer-events:none}
[data-ico-t]:hover::before,[data-ico-t]:hover::after{transform:translateY(-2px)}
[data-ico-t][aria-selected="true"]::before,[data-ico-t][aria-pressed="true"]::before,[data-ico-t].on::before{box-shadow:0 0 0 3px color-mix(in srgb,var(--ico-fg) 22%,transparent),0 3px 8px color-mix(in srgb,var(--ico-fg) 35%,transparent)}
@media (prefers-reduced-motion:reduce){[data-ico-t]::before,[data-ico-t]::after{transition:none}}
[data-ico-i]::before{content:"";display:inline-block;width:22px;height:22px;margin-inline-end:8px;vertical-align:-6px;border-radius:7px;flex:none;
background:var(--ico-fg) var(--ico-w) center/13px 13px no-repeat;box-shadow:0 2px 5px color-mix(in srgb,var(--ico-fg) 28%,transparent),inset 0 1px 0 rgb(255 255 255 / 22%);transition:transform .15s}
a[data-ico-i]:hover::before,button[data-ico-i]:hover::before{transform:translateY(-2px)}
.on[data-ico-i]::before,[aria-pressed="true"][data-ico-i]::before,[aria-selected="true"][data-ico-i]::before,.active[data-ico-i]::before{box-shadow:0 0 0 3px color-mix(in srgb,var(--ico-fg) 22%,transparent),0 3px 8px color-mix(in srgb,var(--ico-fg) 35%,transparent)}
.page-head h1[data-ico-sq]{padding-inline-start:46px!important;min-height:36px}
.page-head h1[data-ico-sq]::before{width:36px;height:36px;border-radius:10px}
.page-head h1[data-ico-sq]::after{inset-inline-start:9px;width:18px;height:18px}
[data-ico-b]::before{content:"";display:inline-block;width:1em;height:1em;margin-inline-end:6px;vertical-align:-2px;background:currentColor;-webkit-mask:var(--ico) center/contain no-repeat;mask:var(--ico) center/contain no-repeat;flex:none}
@media print{[data-ico-b]::before,[data-ico-i]::before,[data-ico-t]::before,[data-ico-t]::after{display:none}[data-ico-t]{padding-inline-start:12px!important}}`;
}

/** Where each style goes. */
const TARGETS: { sel: string; mode: 'sq' | 'k' | 'i' | 't' | 'b'; rules: [RegExp, string][] }[] = [
  { sel: '.panel-head > h3, .panel-head > h2, .list-card > header > h3, .list-card-title, .record-modal-head > h2', mode: 'sq', rules: RULES },
  // Page titles (not on records, which show their own object icon), pop-up and form titles.
  { sel: '.page-head > div:not(.title-with-icon) > h1, .modal-head > h2, .wizard-head > h2, form.modal > h2, .setup-hero h2, .popup-list > h3', mode: 'sq', rules: RULES },
  // KPI names always get an icon: anything no rule matches gets the chart icon.
  { sel: '.kpi-label, .ic-mini-item > span.small.muted:first-child, .kpi-name, .metric > span.muted, .hl-fields dt', mode: 'k', rules: [...RULES, [/./, 'chart']] },
  { sel: '.tabs > a, .setup-nav a, .side-nav a', mode: 'i', rules: RULES },
  // Section tabs (Intelligence, project, accounts, deal intelligence, KPIs, object settings): coloured tiles, like the main tabs.
  { sel: '.ic-tabs > button, .intel-tabs > button, .kpi-tabs > button, .seg[role="tablist"] > button, .ic-dims > button', mode: 't', rules: RULES },
  { sel: '.setup-tile > b', mode: 'i', rules: RULES },
  { sel: '.panel h4:not(.setup-group-head):not(.fill-head):not(.composer-title), .att-group > h4, .guide-topic h4', mode: 'i', rules: RULES },
  { sel: 'button.btn, a.btn', mode: 'b', rules: BUTTONS },
  // Reports: the folder list (every other folder gets the folder icon).
  { sel: '.folder-nav > button', mode: 'i', rules: [[/private/, 'lock'], [/all reports/, 'chart'], [/tender|on hand|on-hand/, 'trend'], [/./, 'folder']] },
];

function tag(root: ParentNode) {
  for (const t of TARGETS) {
    root.querySelectorAll<HTMLElement>(t.sel).forEach((el) => {
      const text = (el.textContent ?? '').trim().toLowerCase();
      if (el.dataset.icoSrc === text) return;
      el.dataset.icoSrc = text;
      // Elements that already carry an icon of their own are left alone.
      const own = el.querySelector('svg, img, .obj-icon');
      const icon = own ? null : pick(text, t.rules);
      for (const m of ['sq', 'k', 'i', 't', 'b']) el.removeAttribute(`data-ico-${m}`);
      if (icon) { el.dataset.ico = icon; el.setAttribute(`data-ico-${t.mode}`, ''); } else el.removeAttribute('data-ico');
    });
  }
}

/** Starts tagging the page and keeps up with what React renders; returns the stop function. */
export function startAutoIcons(): () => void {
  let style = document.getElementById('auto-icons') as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'auto-icons';
    style.textContent = css();
    document.head.appendChild(style);
  }
  let queued = false;
  const run = () => { queued = false; tag(document); };
  const observer = new MutationObserver(() => { if (!queued) { queued = true; requestAnimationFrame(run); } });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  run();
  return () => observer.disconnect();
}
